import { Button } from "@/components/ui/button";
import { useAutumnSubscription } from "@/hooks/useAutumnSubscription";
import { Zap, Rocket, CreditCard, BarChart3, Code2, Download, Layers, Globe, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/components/AuthProvider";

const benefits = [
  {
    icon: "🚀",
    title: "Launch in Minutes",
    description: "Choose your flow type, customize the template, and publish. Your first test can be live in under 15 minutes."
  },
  {
    icon: "📊",
    title: "Real Conversion Data",
    description: "Track clicks, conversions, and drop-off points. See exactly where people stop believing in your offer."
  },
  {
    icon: "💰",
    title: "Save Months of Building",
    description: "Stop building products nobody wants. Test the idea first and only invest in what the market proves it wants."
  },
  {
    icon: "🎯",
    title: "Test Any Business Model",
    description: "Payment, waitlist, pricing, subscription, pre-order — validate any monetization strategy before writing code."
  }
];

const features = [
  "🚀 Up to 12 active flows",
  "💳 All 8 flow types included",
  "📊 Full analytics dashboard",
  "🌐 Embed & direct link publishing",
  "📥 Export conversion data (CSV)",
  "🔄 Regular updates & new features"
];

export function Pricing() {
  const { startSubscription, isLoading, isInTrial, getDaysRemaining, hasAccess, hasLegacyLifetimeAccess } = useAutumnSubscription();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);

  // Redirect to dashboard if user already has access
  useEffect(() => {
    if (hasLegacyLifetimeAccess() || hasAccess("lifetime") || hasAccess("monthly") || hasAccess("yearly")) {
      navigate("/dashboard");
    }
  }, [hasAccess, hasLegacyLifetimeAccess, navigate]);

  const pricingTiers = [
    {
      name: "Monthly",
      price: "19",
      period: "/month",
      description: "For founders who test ideas seriously.",
      features: [
        { icon: Rocket, text: "Up to 12 active flows" },
        { icon: Layers, text: "All 8 flow types (payment, waitlist, pricing & more)" },
        { icon: BarChart3, text: "Full analytics with conversion tracking" },
        { icon: Code2, text: "Embed publishing & direct links" },
        { icon: Globe, text: "Custom branding on flows" },
        { icon: Mail, text: "Priority email support" },
        { icon: Download, text: "Export conversion data (CSV)" },
      ],
      isPopular: false,
      planId: "monthly"
    },
    {
      name: "Yearly",
      price: "9",
      period: "/month",
      billingNote: "Billed yearly at $108",
      description: "Save 53% compared to monthly.",
      features: [
        { icon: Rocket, text: "Up to 12 active flows" },
        { icon: Layers, text: "All 8 flow types (payment, waitlist, pricing & more)" },
        { icon: BarChart3, text: "Full analytics with conversion tracking" },
        { icon: Code2, text: "Embed publishing & direct links" },
        { icon: Globe, text: "Custom branding on flows" },
        { icon: Mail, text: "Priority email support" },
        { icon: Download, text: "Export conversion data (CSV)" },
      ],
      isPopular: true,
      planId: "yearly"
    },
  ];

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <span className="text-2xl mb-3 block">🚀</span>
        <h2 className="text-3xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-violet-600">
          Choose Your Testing Plan
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Start free. Upgrade when you're ready. Every plan includes everything you need to validate your next idea.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {pricingTiers.map((tier) => (
          <div key={tier.name} className={`relative rounded-2xl p-8 bg-white shadow-lg border ${tier.isPopular ? 'border-violet-500 shadow-violet-200' : 'border-gray-200'} transition-all duration-200 hover:shadow-xl`}>
            {tier.isPopular && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-medium px-3 py-1 rounded-full">
                  BEST VALUE
                </span>
              </div>
            )}

            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold mb-3">{tier.name}</h3>
              <p className="text-gray-600 mb-6">{tier.description}</p>

              <div className="mb-4">
                <div className="flex items-baseline justify-center">
                  <span className="text-5xl font-bold text-indigo-600">${tier.price}</span>
                  <span className="text-gray-600">{tier.period}</span>
                </div>

                {/* Prominent Free Trial Badge */}
                <div className="mt-4 mb-2">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-full">
                    <span className="text-lg">🎁</span>
                    <span className="text-sm font-semibold text-green-700">
                      Start FREE for 14 days
                    </span>
                  </div>
                </div>

                <p className="text-xs text-gray-600 mb-1">
                  💳 Card required • $0 charged today
                </p>
                <p className="text-xs text-gray-500">
                  Then ${tier.price}{tier.period} after 14-day trial
                </p>
              </div>
            </div>

            <ul className="space-y-3 mb-8">
              {tier.features.map((feature, idx) => {
                const Icon = feature.icon;
                return (
                  <li key={idx} className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 rounded-md bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-gray-700">{feature.text}</span>
                  </li>
                );
              })}
            </ul>

            <Button
              className="w-full rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white shadow-md hover:shadow-xl transition-all duration-200 h-12 font-semibold"
              onClick={async () => {
                if (!user) {
                  navigate('/auth?returnTo=/subscription');
                } else {
                  setLoadingPlanId(tier.planId);
                  await startSubscription(tier.planId);
                  setLoadingPlanId(null);
                }
              }}
              disabled={isLoading}
            >
              {loadingPlanId === tier.planId ? "Processing..." : "Start Free Trial"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
} 